import {useEffect, useState} from "react";
import api from "../../api/api";
import './styles/Header.css';
import {Link, NavLink, useLocation, useNavigate} from "react-router-dom";

type HeaderData = {
    display_name: string;
    slug: string;
}

type HeaderResponse = {
    message: string;
    ranges: HeaderData[];
};

type HeaderItems = {
    id: number;
    title: string;
    url?: string ;
    subItems?: HeaderItems[];
}

function Header() {
    const [headings, setHeadings] = useState<HeaderItems[]>([]);
    const location = useLocation();
    const navigate = useNavigate();

    async function getHeader() {
        try {
            const response = await api.get<HeaderResponse>('/fetch-header-ranges');

            const header: HeaderItems[] = [
                {
                    id: 1,
                    title: "LET'S COOK",
                    url: "/lets-cook",
                },
                {
                    id: 2,
                    title: "RECIPES",
                    url: "/recipes",
                },
                {
                    id: 3,
                    title: "PRODUCTS",
                    url: "/products",
                    subItems: response.data.ranges.map((range, index) => ({
                        id: index + 1,
                        title: range.display_name,
                        url: `/products/${range.slug}`,
                    })),
                },
                {
                    id: 4,
                    title: "ABOUT US",
                    url: "/about-us",
                },
                {
                    id: 5,
                    title: "FAN RECIPES",
                    url: "/fan-recipes",
                },
                {
                    id: 6,
                    title: "CAMPAIGNS",
                    url: "/campaigns",
                },
                {
                    id: 7,
                    title: "BLOGS",
                    url: "/blogs",
                },
                {
                    id: 8,
                    title: "CONTACT US",
                    url: "/contact-us",
                },
            ];

            setHeadings(header);
        } catch (error) {
            alert(error);
        }
    }

    useEffect(() => {
        getHeader();
    }, []);

    const [menuOpen, setMenuOpen] = useState(false);

    return (
        <div className="header-background">
            <div className="header-inner">
                <div className="header-logo">
                    <a href={"/"}><img src={"/denny logo.svg"} alt="Denny Logo" /></a>
                </div>
                <div className="header-items">
                    {headings.map((item) => (
                        <div key={item.id} className="header-item">
                            <div className="header-item-title">
                                <NavLink
                                    to={item.url!}
                                    end={false}
                                    className={() => {
                                        let isActive =
                                            location.pathname === item.url ||
                                            location.pathname.startsWith(`${item.url}/`);

                                        if (item.url === "/recipes" && location.pathname.startsWith("/recipe/")) {
                                            isActive = true;
                                        }

                                        if (item.url === "/blogs" && location.pathname.startsWith("/blog/")) {
                                            isActive = true;
                                        }

                                        return `header-item-link ${isActive ? "active" : ""}`;
                                    }}
                                >
                                    {item.title}
                                </NavLink>

                                {item.subItems && item.subItems.length > 0 && (
                                    <svg
                                        className="header-arrow"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            d="M6 9l6 6 6-6"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                )}
                            </div>

                            {item.subItems && item.subItems.length > 0 && (
                                <div className="header-dropdown">
                                    {item.subItems.map((subItem) => (
                                        <NavLink
                                            key={subItem.id}
                                            to={subItem.url!}
                                            className={({ isActive }) =>
                                                `header-dropdown-item ${isActive ? "active" : ""}`
                                            }
                                        >
                                            {subItem.title}
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="header-socials">
                    <div className="social-icon" onClick={() =>
                        window.open(
                            'https://www.facebook.com/dennysouthafrica/',
                            '_blank',
                            'noopener,noreferrer'
                        )
                    }>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="#022161" d="M13.5 22v-8h2.7l.4-3h-3.1V9.1c0-.9.3-1.6 1.6-1.6h1.7V4.8c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.5-4 4.2V11H8v3h2.7v8h2.8z"/>
                        </svg>
                    </div>
                    <div className="social-icon" onClick={() =>
                        window.open(
                            'https://www.instagram.com/dennysouthafrica/',
                            '_blank',
                            'noopener,noreferrer'
                        )
                    }>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="#022161" d="M7 2C4.2 2 2 4.2 2 7v10c0 2.8 2.2 5 5 5h10c2.8 0 5-2.2 5-5V7c0-2.8-2.2-5-5-5H7zm10 2c1.7 0 3 1.3 3 3v10c0 1.7-1.3 3-3 3H7c-1.7 0-3-1.3-3-3V7c0-1.7 1.3-3 3-3h10zm-5 3.5A4.5 4.5 0 1 0 16.5 12 4.5 4.5 0 0 0 12 7.5zm0 2A2.5 2.5 0 1 1 9.5 12 2.5 2.5 0 0 1 12 9.5zm5-3a1 1 0 1 0 1 1 1 1 0 0 0-1-1z"/>
                        </svg>
                    </div>
                    <div className="social-icon" onClick={() =>
                        window.open(
                            'https://www.youtube.com/@Denny_SouthAfrica',
                            '_blank',
                            'noopener,noreferrer'
                        )
                    }>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="#022161" d="M23 12s0-3.1-.4-4.5c-.2-.8-.9-1.5-1.7-1.7C19.5 5.4 12 5.4 12 5.4s-7.5 0-8.9.4c-.8.2-1.5.9-1.7 1.7C1 8.9 1 12 1 12s0 3.1.4 4.5c.2.8.9 1.5 1.7 1.7 1.4.4 8.9.4 8.9.4s7.5 0 8.9-.4c.8-.2 1.5-.9 1.7-1.7.4-1.4.4-4.5.4-4.5zm-13 3.2V8.8L16 12l-6 3.2z"/>
                        </svg>
                    </div>
                </div>

            </div>
            <div className="mobile-header-inner">
                <div className="mobile-logo">
                    <Link to="/">
                        <img src="/denny logo.svg" alt="Denny Logo" />
                    </Link>
                </div>

                <div className="mobile-header-right">
                    <button
                        className="mobile-menu-button"
                        onClick={() => setMenuOpen(!menuOpen)}
                    >
                        {menuOpen ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="17.121" height="17.121" viewBox="0 0 17.121 17.121">
                                <path id="Path_19059" data-name="Path 19059" d="M6,21,21,6M6,6,21,21" transform="translate(-4.939 -4.939)" fill="none" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
                            </svg>

                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="31.5" height="13.5" viewBox="0 0 31.5 13.5">
                                <path id="Path_19058" data-name="Path 19058" d="M3.75,9h30m-30,12h30" transform="translate(-3 -8.25)" fill="none" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
                            </svg>

                        )}
                    </button>
                </div>
            </div>

            {menuOpen && (
                <div className="mobile-menu">
                    {headings.map((item) => (
                        <NavLink
                            key={item.id}
                            to={item.url!}
                            className={() => {
                                let isActive =
                                    location.pathname === item.url ||
                                    location.pathname.startsWith(`${item.url}/`);

                                if (item.url === "/product" && location.pathname.startsWith("/product/")) {
                                    isActive = true;
                                }

                                if (item.url === "/recipes" && location.pathname.startsWith("/recipe/")) {
                                    isActive = true;
                                }

                                if (item.url === "/blogs" && location.pathname.startsWith("/blog/")) {
                                    isActive = true;
                                }

                                return `mobile-menu-link ${isActive ? "active" : ""}`;
                            }}
                            onClick={() => setMenuOpen(false)}
                        >
                            {item.title}
                        </NavLink>
                    ))}

                    <div className="mobile-menu-footer">
                        LEARN MORE AT
                        <a
                            href="https://www.libstar.co.za"
                            className="libstar-link-head"
                        >
                            WWW.LIBSTAR.CO.ZA
                        </a>
                        {" | "}
                        &reg;{new Date().getFullYear()} COPYRIGHT OF DENNY&reg;.
                        {" | "}
                        ALL RIGHTS RESERVED.
                    </div>
                </div>
            )}
        </div>
    )
}

export default Header;
